
import Component from "vue-class-component";
import Vue from "vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import {
    AddInlandVesselPracticeRequest,
    InlandVesselPracticeActions,
    PracticeKind,
    PositionOption,
    OrganizationOption,
    PersonOption,
    PositionsClient,
    PracticesClient,
    AreasClient,
    AreaOption,
    PracticePeriod,
    ResultError,
    CountryLightModel,
    DocumentAttributesClient,
    ShipTypeLocalizedModel,
    ShipTypesClient
} from "@/api-client";
import PracticePeriods from "@/features/documents/practices/components/PracticePeriods.vue";
import ShipInformation from "@/features/documents/practices/components/ShipInformation.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {Prop} from "vue-property-decorator";
import Loader from "@/features/shared/components/Loader";
import toasts from "@/components/Toasts";
import moment from "moment";
import userInfo from "@/features/shared/components/UserInfo";
import Files from "@/components/Files.vue";
import FileUploader from "@/components/FileUploader.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import DropdownSelector from "@/components/DropdownSelector.vue";
import ConfirmModal from "@/features/shared/components/ConfirmModal.vue";

@Component({
    components: {
        CheckboxInput,
        ConfirmModal,
        DropdownSelector,
        Files,
        FileUploader,
        HeadingGroup,
        LabelOptional,
        PracticePeriods,
        ShipInformation,
    }
})
export default class InlandVesselPracticeUpsertForm extends Vue {
    @Prop({required: true})
    seafarerId!: string;

    @Prop({required: true})
    model!: AddInlandVesselPracticeRequest;

    @Prop({required: true})
    actions!: InlandVesselPracticeActions;

    @Prop({required: true})
    submitCallback!: (model: AddInlandVesselPracticeRequest) => Promise<void>;

    validationErrors = ResultError.createEmpty();

    warningMatching = false;
    
    isOrganizationEnteredManually = false;
    isConfirmerNameInsertedManually = false;

    positionOptions: PositionOption[] = [];
    organizationOptions: OrganizationOption[] = [];
    areaOptions: AreaOption[] = [];
    shipTypeOptions: ShipTypeLocalizedModel[] = [];
    countries: CountryLightModel[] = [];


    fileLoader = new Loader();
    submitLoader = new Loader();
    isCreated = false;
    isSeafarer = userInfo.seafarerId === this.seafarerId;
    
    confirmerOptions: PersonOption[] = [];
    
    get practiceKindOptions() {
        return [
            { text: this.$ls.Documents_Practices_Label_Educational, value: PracticeKind.Educational },
            { text: this.$ls.Documents_Practices_Label_Working, value: PracticeKind.Work }
        ];
    }

    async created() {
        // Initialize checkbox state based on model values
        // Check for null GUID (00000000-0000-0000-0000-000000000000) as well as null/undefined
        const isNullGuid = this.model.organizationId === "00000000-0000-0000-0000-000000000000";
        this.isOrganizationEnteredManually = (!this.model.organizationId || isNullGuid) && !!this.model.organizationName;
        this.isConfirmerNameInsertedManually = !this.model.confirmerId && !!this.model.confirmer;
        
        await Promise.all(
            [
                new PositionsClient().activePositionOptions().then(x => this.positionOptions = x),
                new PracticesClient().inlandVesselOrganizationOptions().then(x => {
                    this.organizationOptions = x.organizationOptions.filter(o => o.active);
                    
                    // Set default organization and confirmer if user can't choose
                    if (this.organizationOptions.length > 0 && !this.actions.canChooseOrganizationAndConfirmer) {
                        this.confirmerOptions = this.organizationOptions[0].personOptions;
                        if (this.confirmerOptions && this.confirmerOptions.length > 0) {
                            this.model.confirmerId = this.confirmerOptions[0].value;
                        }
                        this.model.organizationId = this.organizationOptions[0].value;
                        this.model.organizationName = this.organizationOptions[0].text;
                    } else if (this.model.organizationId) {
                        // Load confirmer options for the initially selected organization
                        this.setConfirmerOptions();
                    }
                }),
                new AreasClient().inlandVesselAreaOptions().then(x => {
                    // Filter to show only Inland Waters type areas
                    this.areaOptions = x.areas;
                }),
                new DocumentAttributesClient().countries().then(x => this.countries = x),
                new ShipTypesClient().shipTypesLocalized("").then(x => this.shipTypeOptions = x.shipTypesLocalized.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)))
            ]
        );

        // Always set practice kind to Work for inland vessel practice
        this.model.kind = PracticeKind.Work;

        // Initialize empty harbourVisits array only if not already present
        if (!this.model.harbourVisits) {
            this.model.harbourVisits = [];
        }

        // Initialize practice certificate setting
        // If user can't choose, default to true (file required)
        // If user can choose, default to false (no file)
        if (this.model.withPracticeCertificate === undefined) {
            this.model.withPracticeCertificate = !this.actions.canChooseWithOrWithoutFile;
        }

        // Initialize files arrays if not present
        if (!this.model.addedFiles) {
            this.model.addedFiles = [];
        }
        if (!this.model.existingFiles) {
            this.model.existingFiles = [];
        }
        if (!this.model.removedFiles) {
            this.model.removedFiles = [];
        }

        // Initialize practice periods if not present
        if (!this.model.practicePeriods) {
            this.model.practicePeriods = [];
        }

        this.$watch(() => this.model, () => {
            this.validationErrors.clearKey("practicePeriods");
            if(this.model.issuedOn && moment().startOf("day").diff(this.model.issuedOn) < 0){
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issuedOn");
            }
            else {
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.clear();
                if (this.model.issuedOn && this.model.practicePeriods.length > 0) {
                    this.model.practicePeriods.forEach((period: PracticePeriod) => {
                        if (moment(period.startedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                        if (period.endedOn && moment(period.endedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                    });
                }
            }
        },{deep:true});
        
        this.$watch(
            () => this.isOrganizationEnteredManually,
            () => {
                if (this.isOrganizationEnteredManually) {
                    this.model.organizationId = null;
                    this.model.confirmerId = null;
                    this.confirmerOptions = [];
                    this.isConfirmerNameInsertedManually = true;
                } else {
                    this.model.organizationName = null;
                    this.isConfirmerNameInsertedManually = false;
                }
            }
        );
        
        this.$watch(
            () => this.model.organizationId,
            () => {
                if (this.model.organizationId && !this.isOrganizationEnteredManually) {
                    this.setConfirmerOptions();
                } else if (!this.model.organizationId) {
                    this.confirmerOptions = [];
                    this.isConfirmerNameInsertedManually = true;
                }
            }
        );
        
        this.$watch(
            () => this.isConfirmerNameInsertedManually,
            () => {
                if (this.isConfirmerNameInsertedManually) {
                    this.model.confirmerId = null;
                } else {
                    this.model.confirmer = null;
                }
            }
        );
        
        this.isCreated = true;
    }

    get canSubmit() {
        return this.validationErrors.isEmpty && !this.submitLoader.loading;
    }
    
    get hasAnyConfirmerOptions() {
        return this.confirmerOptions && this.confirmerOptions.length > 0;
    }
    
    get hasNoConfirmerOptions() {
        return !this.hasAnyConfirmerOptions;
    }
    
    translatePracticeKind(kind: PracticeKind): string {
        switch (kind) {
            case PracticeKind.Educational:
                return this.$ls.Documents_Practices_PracticeKind_Educational;
            case PracticeKind.Work:
                return this.$ls.Documents_Practices_PracticeKind_Work;
            default:
                return "";
        }
    }

    private addPracticePeriod(period: PracticePeriod) {
        this.model.practicePeriods.push(period);
    }

    private removePracticePeriod(practicePeriod: PracticePeriod) {
        this.$delete(
            this.model.practicePeriods,
            this.model.practicePeriods.indexOf(practicePeriod)
        );
    }
    
    private setConfirmerOptions() {
        const organization = this.organizationOptions.find(o => o.value == this.model.organizationId);
        this.confirmerOptions = organization ? organization.personOptions : [];
        
        // If no confirmer options available, force manual entry
        if (this.confirmerOptions.length === 0) {
            this.isConfirmerNameInsertedManually = true;
        }
    }


    private async submit() {
        if (!this.canSubmit) {
            return;
        }
        
        // Ensure withPracticeCertificate is set correctly based on whether files are present
        if (!this.actions.canChooseWithOrWithoutFile && (this.model.addedFiles?.length > 0 || this.model.existingFiles?.length > 0)) {
            this.model.withPracticeCertificate = true;
        }
        
        // If organization is entered manually, ensure organizationId is null (not null GUID)
        if (this.isOrganizationEnteredManually) {
            this.model.organizationId = null;
        }

        await this.submitLoader.startLoading();
        this.validationErrors.clear();
        await this.submitCallback(this.model)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
            })
            .catch((error: ResultError) => {
                this.validationErrors = error;
                toasts.failure(this.$ls.Global_Toast_FormSaveFailure);
            })
        this.submitLoader.endLoading();
    }
}
